/**
 * FormStepScreen: the booking flow's form page (registration and the studio's forms), shared by the
 * Mini App's DetailsPage and the studio website's phone checkout (shared-client-screens D3, rows R1
 * to R9). Props only: the pinned photo header, the form's editorial header (title, kind line,
 * description, the form-count dots), the hold line, the form itself (FormRenderer), the consent row a
 * registration carries (PrivacyConsentRowView, passed in) and the floating CTA. While its reads are
 * in flight it says what is loading (row R4); a read that failed says so with Go Back (row R3).
 */
import type { ReactNode, Ref } from 'react';
import { BookingHero, type BookingHeroProps } from '@/components/booking/BookingHero';
import { BookingStepFrame } from '@/components/booking/BookingStepFrame';
import { FormRenderer } from '@/components/forms/FormRenderer';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';
import { ctaLabel, formTypeLine } from './formHeader';
import styles from './DetailsPage.module.css';

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/** What the page shows instead of a form: what is loading, or a read that failed and its way out. */
export type FormStepState =
  | { readonly kind: 'loading'; readonly text: string }
  | { readonly kind: 'error'; readonly title: string; readonly message: string; readonly action?: { readonly label: string; readonly onPress: () => void } };

/**
 * A failed forms read in the app's words (row R3): the flow names forms it cannot show ("Unable to
 * Load Forms" with its reason), or the form itself could not be read ("Unable to Load Form"). Go Back
 * takes the step back. Null when nothing failed.
 */
export function formFailure(
  forms: { readonly isError: boolean; readonly errorMessage: string | null },
  formFailed: boolean,
  onBack: () => void,
): FormStepState | null {
  const action = { label: 'Go Back', onPress: onBack };
  if (forms.isError) return { kind: 'error', title: 'Unable to Load Forms', message: forms.errorMessage ?? '', action };
  if (formFailed) return { kind: 'error', title: 'Unable to Load Form', message: 'Please try again or go back.', action };
  return null;
}

/** The form page's placeholder or error (rows R3, R4), without the photo header, as the app draws them. */
export function FormStepPlaceholder({ state }: { state: FormStepState }) {
  if (state.kind === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>{state.text}</p>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.page}>
      <div className={styles.error}>
        <h2>{state.title}</h2>
        <p>{state.message}</p>
        {state.action && <button onClick={state.action.onPress}>{state.action.label}</button>}
      </div>
    </div>
  );
}

export interface FormStepScreenProps {
  hero: BookingHeroProps;
  onBack: () => void;
  /** Back is off while a step back is under way (row R5). */
  backDisabled?: boolean;
  /** The scroller, for the surface's keyboard rail. */
  scrollRef?: Ref<HTMLDivElement>;
  form: FormDefinition;
  /** 1-based place of this form among all the client owes, and how many. */
  index: number;
  total: number;
  /** The hold line ("Time slot reserved until 6:15 PM"); null without one. */
  reserved: string | null;
  /** Payment follows the forms (the CTA's words). */
  paymentAhead: boolean;
  submitting: boolean;
  serverErrors: FieldError[] | undefined;
  onSubmit: (responses: FormResponseInput[]) => void;
  onSave: (responses: FormResponseInput[]) => void;
  /** Above the CTA: a registration's privacy agreement (PrivacyConsentRowView). */
  beforeButton?: ReactNode;
}

export function FormStepScreen({
  hero,
  onBack,
  backDisabled,
  scrollRef,
  form,
  index,
  total,
  reserved,
  paymentAhead,
  submitting,
  serverErrors,
  onSubmit,
  onSave,
  beforeButton,
}: FormStepScreenProps) {
  return (
    // Icon-only glass Back at the catalog/detail screen position (§3-C); the hero sits where the
    // detail page's does, so the image stays in place screen-to-screen.
    <BookingStepFrame styles={styles} onBack={onBack} backDisabled={backDisabled} scrollRef={scrollRef} hero={<BookingHero {...hero} />}>
      {/* Editorial form header + form-count dots (§3-D). */}
      <div className={styles.fhead}>
        <div className={styles.fheadTop}>
          <div className={styles.ttl}>
            <h1 className={styles.fname}>{form.title}</h1>
            <div className={styles.req}>{formTypeLine(form.formType)}</div>
          </div>
          {total >= 2 && (
            <div className={styles.fcount}>
              <div className={styles.dots}>
                {Array.from({ length: total }).map((_, i) => (
                  <i key={i} className={i === index - 1 ? styles.dotOn : undefined} />
                ))}
              </div>
              <div className={styles.countLbl}>
                {index} of {total} forms
              </div>
            </div>
          )}
        </div>
        {form.description && <div className={styles.fdesc}>{form.description}</div>}

        {reserved && (
          <div className={styles.reservedInfo}>
            <ClockIcon />
            {reserved}
          </div>
        )}
      </div>

      <FormRenderer
        form={form}
        onSubmit={onSubmit}
        onSave={onSave}
        isSubmitting={submitting}
        serverErrors={serverErrors}
        // Registration forms do not save partway.
        disableAutoSave={form.formType === 'REGISTRATION'}
        appearance="clientBooking"
        renderActions={({ formReady, isSubmitting }) => (
          <>
            {beforeButton}
            <div className={styles.formbar}>
              <button type="submit" className={styles.btnPrimary} disabled={isSubmitting}>
                {ctaLabel({ formType: form.formType, isLastForm: index === total, hasPaymentAhead: paymentAhead, formReady })}
                <ArrowRightIcon />
              </button>
            </div>
          </>
        )}
      />
    </BookingStepFrame>
  );
}
